import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { label: "Start", href: "#home" },
  { label: "Story", href: "#story" },
  { label: "Tools", href: "#tools" },
  { label: "Benefits", href: "#benefits" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dynamic colors: light text over dark hero, dark text after scroll
  const textColor = scrolled ? "text-gray-700" : "text-slate-200/85";
  const textHover = scrolled ? "hover:text-[#202A36]" : "hover:text-white";
  const brandColor = scrolled ? "text-[#202A36]" : "text-white";
  const brandBg = scrolled ? "bg-[#202A36] text-white" : "bg-white text-[#0b1220]";
  const ctaBg = scrolled
    ? "bg-[#202A36] text-white hover:bg-[#1a2229]"
    : "bg-white text-[#0b1220] hover:bg-sky-50";
  const underlineColor = scrolled ? "bg-[#202A36]" : "bg-sky-300";

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-black/5"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span
            className={`relative inline-flex h-8 w-8 items-center justify-center rounded-md font-display font-bold text-sm tracking-tighter transition-colors ${brandBg}`}
          >
            A
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          </span>
          <span
            className={`font-display text-xl md:text-[22px] font-bold tracking-tight transition-colors ${brandColor}`}
          >
            Audivis
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className={`relative px-4 py-2 text-sm font-medium transition-colors group ${textColor} ${textHover}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
              <span
                className={`absolute left-4 right-4 -bottom-0.5 h-px ${underlineColor} origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}
              />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="/hub/"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${ctaBg}`}
            data-testid="nav-signin"
          >
            Sign in
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className={`md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
            scrolled
              ? "border border-black/10 bg-white/80 text-[#202A36]"
              : "border border-white/15 bg-white/5 text-white backdrop-blur"
          }`}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden mx-4 mb-4 rounded-2xl bg-white/95 backdrop-blur-md shadow-xl border border-black/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-2">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-[#202A36] font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/hub/"
                className="mt-2 mx-2 px-4 py-3 rounded-full bg-[#202A36] text-white text-center font-medium"
                data-testid="mobile-cta-launch"
              >
                Launch suite →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
